import React, { useCallback, useEffect, useState } from "react";
import { connect, shallowEqual, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import { useIntl } from "react-intl";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import * as appPropTypes from "../../typings/appPropTypes";
import * as roomActions from "../../actions/roomActions";
import VideoView from "../Presence/Media/VideoView";
import Peer from "../Presence/Peer";
import Me from "../Presence/Me";
import { makeSpotlightPeersSelector } from "../../helpers/Selectors";
import {
  ROOM_MINI_TOGGLE_RESERVE_MINI_PX,
  ROOM_MINI_TOGGLE_RESERVE_PX,
  useConversationMiniToggleVisible,
} from "../../helpers/remoteControl/roomChrome";

const styles = () => ({
  root: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    "& video": {
      borderRadius: 0,
    },
    paddingBottom: 80,
  },
  controls: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 20025,
  },
  controlsReserveMiniToggle: {
    right: ROOM_MINI_TOGGLE_RESERVE_PX,
  },
  miniControls: {
    top: 6,
    right: 6,
  },
  miniControlsReserveMiniToggle: {
    right: ROOM_MINI_TOGGLE_RESERVE_MINI_PX,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: 4,
    borderRadius: 10,
    background: "rgba(24, 28, 30, 0.72)",
    backdropFilter: "blur(6px)",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.18)",
  },
  miniToolbar: {
    padding: 2,
    gap: 2,
    borderRadius: 6,
  },
  iconButton: {
    color: "#fff",
    padding: 6,
    "&:hover": {
      background: "rgba(255, 255, 255, 0.12)",
    },
  },
  miniIconButton: {
    padding: 4,
  },
  incompatibleVideo: {
    position: "absolute",
    zIndex: 20010,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    "& p": {
      padding: "6px 12px",
      borderRadius: 6,
      userSelect: "none",
      pointerEvents: "none",
      fontSize: 15,
      color: "rgba(255, 255, 255, 0.55)",
    },
  },
  videoStrip: {
    display: "flex",
    flexDirection: "column",
    marginTop: 80,
    overflowY: "auto",
  },
  videoContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
  },
});

const FullScreenView = (props) => {
  const { consumer, peers, classes, toggleConsumerFullscreen } = props;
  const intl = useIntl();
  const isMini = useSelector((state) => state.call.displayMini);
  const reserveMiniToggle = useConversationMiniToggleVisible();

  const [lastSpeaker, setLastSpeaker] = useState();
  const spotlights = useSelector(
    (state) => makeSpotlightPeersSelector(state)(state),
    shallowEqual
  );
  const room = useSelector((state) => state.room);
  const activeSpeakerId = useSelector((state) => state.room.activeSpeakerId);
  const myId = useSelector((state) => state.me.id);

  const getActivePeerId = useCallback(() => {
    if (lastSpeaker && peers[lastSpeaker]) {
      return lastSpeaker;
    }
    const peerIds = Object.keys(peers);

    if (peerIds.length > 0) {
      return peerIds[0];
    }
  }, [peers, lastSpeaker]);
  const activePeerId = getActivePeerId();

  useEffect(() => {
    if (activeSpeakerId != null && activeSpeakerId !== myId) {
      setLastSpeaker(activeSpeakerId);
    }
  }, [activeSpeakerId, myId]);

  if (!consumer) return null;

  const consumerVisible =
    Boolean(consumer) && !consumer.locallyPaused && !consumer.remotelyPaused;

  const controlsClass = [
    classes.controls,
    isMini ? classes.miniControls : null,
    reserveMiniToggle
      ? isMini
        ? classes.miniControlsReserveMiniToggle
        : classes.controlsReserveMiniToggle
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes.root}>
      <div className={controlsClass}>
        <div
          className={`${classes.toolbar} ${isMini ? classes.miniToolbar : ""}`}
        >
          <Tooltip
            title={intl.formatMessage({
              id: "tooltip.leaveFullscreen",
              defaultMessage: "Leave fullscreen",
            })}
          >
            <IconButton
              size="small"
              className={`${classes.iconButton} ${
                isMini ? classes.miniIconButton : ""
              }`}
              disabled={!consumerVisible}
              onClick={() => toggleConsumerFullscreen(consumer)}
            >
              <FullscreenExitIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className={classes.videoContainer}>
        <div className={classes.videoStrip}>
          {window.innerHeight > 650 &&
            window.innerWidth > 420 && (
              <Me
                style={{
                  width: 180,
                  height: 135,
                  backgroundSize: "contain",
                  borderRadius: 0,
                }}
                mini={true}
                onFilmStrip={true}
              />
            ) && (
              <div
                style={{ borderRadius: 24 }}
                //key={peerId}
                /*              className={classnames(classes.videoStrip, {
                  /!*active: peerId === activePeerId,*!/
                })}*/
              >
                <Me
                  style={{
                    width: 180,
                    height: 135,
                    backgroundSize: "contain",
                    borderRadius: 0,
                  }}
                  mini={true}
                  onFilmStrip={true}
                />

                {peers[activePeerId] &&
                  (!room.webinar ||
                    (room.webinar && spotlights.length !== 0)) && (
                    <Peer
                      id={activePeerId}
                      style={{
                        width: 180,
                        height: 135,
                        backgroundSize: "contain",
                      }}
                      mini={true}
                      onFilmStrip={true}
                      excludeConsumer={consumer}
                      makeFullScreenOnClick={true}
                    />
                  )}

                {Object.keys(peers).map((peerId) => {
                  if (
                    spotlights.find(
                      (spotlightsElement) => spotlightsElement === peerId
                    ) &&
                    activePeerId !== peerId
                  ) {
                    return (
                      <Peer
                        id={peerId}
                        style={{
                          width: 180,
                          height: 135,
                          backgroundSize: "contain",
                        }}
                        mini={true}
                        onFilmStrip={true}
                        excludeConsumer={consumer}
                        makeFullScreenOnClick={true}
                      />
                    );
                  } else {
                    return "";
                  }
                })}
              </div>
            )}
        </div>

        <VideoView
          style={{ marginLeft: 100 }}
          videoContain
          isScreen={Boolean(consumer && consumer.source === "screen")}
          peer={consumer && peers[consumer.peerId]}
          videoTrack={consumer ? consumer.track : null}
          videoVisible={consumerVisible}
        />
      </div>
    </div>
  );
};

FullScreenView.propTypes = {
  consumer: appPropTypes.Consumer,
  toggleConsumerFullscreen: PropTypes.func.isRequired,
  toolbarsVisible: PropTypes.bool,
  permanentTopBar: PropTypes.bool,
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  consumer: state.consumers[state.room.fullScreenConsumer],
  toolbarsVisible: state.room.toolbarsVisible,
  permanentTopBar: state.settings.permanentTopBar,
  peers: state.peers,
});

const mapDispatchToProps = (dispatch) => ({
  toggleConsumerFullscreen: (consumer) => {
    if (consumer) dispatch(roomActions.toggleConsumerFullscreen(consumer.id));
  },
});

export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatesEqual: (next, prev) => {
    return (
      prev.consumers[prev.room.fullScreenConsumer] ===
        next.consumers[next.room.fullScreenConsumer] &&
      prev.room.toolbarsVisible === next.room.toolbarsVisible &&
      prev.settings.permanentTopBar === next.settings.permanentTopBar &&
      prev.peers === next.peers
    );
  },
})(withStyles(styles)(FullScreenView));
